import { GESTURE_INTERACTION_CONFIG } from './gestureConfig.js'

/**
 * Screen-space magnetic targeting for Cesium reservoir entities.
 * Cesium projection is deliberately injected so this state machine remains
 * deterministic and testable without a browser or a Cesium scene.
 */
export const RESERVOIR_TARGET_STATES = Object.freeze({
  FREE: 'FREE',
  NEAR_TARGET: 'NEAR_TARGET',
  SNAPPED: 'SNAPPED',
  LOCKED: 'LOCKED',
})

function finite(value) {
  return Number.isFinite(Number(value)) ? Number(value) : null
}

function distance(a, b) {
  return Math.hypot(a.x - b.x, a.y - b.y)
}

function cloneTarget(target) {
  return target ? { ...target, data: target.data ? { ...target.data } : target.data } : null
}

export class ReservoirTargetResolver {
  constructor({
    getTargets = () => [],
    snapRadiusPx = GESTURE_INTERACTION_CONFIG.snapRadiusPx,
    releaseRadiusPx = GESTURE_INTERACTION_CONFIG.releaseRadiusPx,
    stableMs = GESTURE_INTERACTION_CONFIG.targetStableMs,
    smoothingMs = GESTURE_INTERACTION_CONFIG.smoothingMs,
    switchMarginPx = GESTURE_INTERACTION_CONFIG.switchMarginPx,
    now = () => Date.now(),
  } = {}) {
    this.getTargets = getTargets
    this.snapRadiusPx = snapRadiusPx
    this.releaseRadiusPx = Math.max(releaseRadiusPx, snapRadiusPx)
    this.stableMs = stableMs
    this.smoothingMs = smoothingMs
    this.switchMarginPx = switchMarginPx
    this.now = now
    this.state = RESERVOIR_TARGET_STATES.FREE
    this.hoverTarget = null
    this.selectedTarget = null
    this.snappedTarget = null
    this.candidateSince = 0
    this.lastUpdateAt = 0
    this.visualPosition = null
    this.rawPosition = null
  }

  update(pointer = {}) {
    const x = finite(pointer.x)
    const y = finite(pointer.y)
    const now = finite(pointer.timestamp) || this.now()
    if (x === null || y === null || pointer.active === false || pointer.controlEnabled === false) {
      return this._result(false)
    }

    const raw = { x, y }
    this.rawPosition = raw
    const targets = this._visibleTargets()
    const nearest = this._nearest(raw, targets)
    const retained = this._retainSnapped(raw, targets)
    const nextTarget = retained || (nearest && nearest.distance <= this.snapRadiusPx ? nearest.target : null)

    if (this.snappedTarget && !retained) {
      this.snappedTarget = null
      this.candidateSince = 0
      if (!this.selectedTarget) this.state = RESERVOIR_TARGET_STATES.FREE
    }

    if (!this.snappedTarget && nextTarget) {
      if (!this.hoverTarget || this.hoverTarget.key !== nextTarget.key) {
        this.hoverTarget = nextTarget
        this.candidateSince = now
        this.state = this.selectedTarget
          ? RESERVOIR_TARGET_STATES.LOCKED
          : RESERVOIR_TARGET_STATES.NEAR_TARGET
      } else if (now - this.candidateSince >= this.stableMs) {
        this.snappedTarget = nextTarget
        this.state = this.selectedTarget
          ? RESERVOIR_TARGET_STATES.LOCKED
          : RESERVOIR_TARGET_STATES.SNAPPED
      }
    } else if (this.snappedTarget) {
      this.hoverTarget = nextTarget || this.snappedTarget
      if (this.selectedTarget) this.state = RESERVOIR_TARGET_STATES.LOCKED
      else this.state = RESERVOIR_TARGET_STATES.SNAPPED
    } else if (!nextTarget) {
      this.hoverTarget = null
      if (!this.selectedTarget) this.state = RESERVOIR_TARGET_STATES.FREE
    }

    const targetPosition = this.hoverTarget && this.hoverTarget.screenPosition
    const destination = targetPosition && this.state !== RESERVOIR_TARGET_STATES.FREE
      ? { x: targetPosition.x, y: targetPosition.y }
      : raw
    this.visualPosition = this._smooth(destination, now)
    this.lastUpdateAt = now
    return this._result(true)
  }

  lock() {
    if (!this.snappedTarget || this.state === RESERVOIR_TARGET_STATES.NEAR_TARGET) return null
    this.selectedTarget = cloneTarget(this.snappedTarget)
    this.state = RESERVOIR_TARGET_STATES.LOCKED
    return cloneTarget(this.selectedTarget)
  }

  unlock() {
    const previous = cloneTarget(this.selectedTarget)
    this.selectedTarget = null
    this.state = this.snappedTarget
      ? RESERVOIR_TARGET_STATES.SNAPPED
      : RESERVOIR_TARGET_STATES.FREE
    return previous
  }

  clear() {
    this.hoverTarget = null
    this.snappedTarget = null
    this.selectedTarget = null
    this.candidateSince = 0
    this.state = RESERVOIR_TARGET_STATES.FREE
    return this._result(false)
  }

  snapshot() {
    return this._result(false)
  }

  getDebugState(rawPointer = null) {
    const raw = rawPointer || this.rawPosition || { x: 0, y: 0 }
    const targets = this._visibleTargets()
    const nearest = this._nearest(raw, targets)
    return {
      pointerX: Math.round(raw.x || 0),
      pointerY: Math.round(raw.y || 0),
      state: this.state,
      nearestTarget: nearest ? nearest.target.displayName || nearest.target.name : '无',
      distance: nearest ? Math.round(nearest.distance) : 9999,
      hoverTarget: this.hoverTarget ? this.hoverTarget.displayName || this.hoverTarget.name : '无',
      selectedTarget: this.selectedTarget ? this.selectedTarget.displayName || this.selectedTarget.name : 'null',
      snapped: Boolean(this.snappedTarget),
      locked: Boolean(this.selectedTarget),
    }
  }

  _visibleTargets() {
    let targets = []
    try { targets = this.getTargets() || [] } catch (_) { targets = [] }
    return targets.filter(target => target && target.visible !== false
      && target.screenPosition
      && finite(target.screenPosition.x) !== null
      && finite(target.screenPosition.y) !== null)
  }

  _nearest(raw, targets) {
    if (!targets.length) return null
    return targets.reduce((best, target) => {
      const next = { target, distance: distance(raw, target.screenPosition) }
      return !best || next.distance < best.distance ? next : best
    }, null)
  }

  _retainSnapped(raw, targets) {
    if (!this.snappedTarget) return null
    const current = targets.find(target => target.key === this.snappedTarget.key)
    if (!current) return null
    const currentDistance = distance(raw, current.screenPosition)
    if (currentDistance > this.releaseRadiusPx) return null
    const nearest = this._nearest(raw, targets)
    if (!nearest || nearest.target.key === current.key) return current
    return nearest.distance + this.switchMarginPx < currentDistance ? nearest.target : current
  }

  _smooth(destination, now) {
    if (!this.visualPosition || this.smoothingMs <= 0) return { ...destination }
    const elapsed = Math.max(0, now - (this.lastUpdateAt || now))
    const alpha = Math.min(1, elapsed / this.smoothingMs)
    return {
      x: this.visualPosition.x + (destination.x - this.visualPosition.x) * alpha,
      y: this.visualPosition.y + (destination.y - this.visualPosition.y) * alpha,
    }
  }

  _result(active) {
    return {
      active,
      state: this.state,
      hoverTarget: cloneTarget(this.hoverTarget),
      selectedTarget: cloneTarget(this.selectedTarget),
      snapped: Boolean(this.snappedTarget),
      locked: Boolean(this.selectedTarget),
      rawPosition: this.rawPosition ? { ...this.rawPosition } : null,
      screenPosition: this.visualPosition ? { ...this.visualPosition } : this.rawPosition,
    }
  }
}

export default ReservoirTargetResolver
